/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { HealthTokenRenderer } from 'src/languageStudio/appShell/prebuiltDemos/taForHealth/HealthTokenRenderer';
import { HealthTokenStore } from 'src/languageStudio/appShell/prebuiltDemos/taForHealth/HealthTokenStore';
import { DocumentHealthcareEntities, HealthcareEntity } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { getEntityColor } from 'src/languageStudio/core/utils/demosUtils';
import { AnnotationData, ITokenRendererProps, Labeler } from 'src/packages/react-text-annotator/lib/esm';

export type TaForHealthLabelerContainerProps = {
    document: string;
    isLabelerRTL: boolean;
    isLabelerWordTokenized: boolean;
    results: DocumentHealthcareEntities;
};

const messages = defineMessages({
    negative: { id: 'languageStudio.taForHealthLabelerContainer.negative', defaultMessage: 'Negative' },
    textTitle: { id: 'languageStudio.taForHealthLabelerContainer.textTitle', defaultMessage: 'Original Text' },
    changeCoding: { id: 'languageStudio.taForHealthLabelerContainer.changeCoding', defaultMessage: 'Change coding system' },
    relationShipsToggle: { id: 'languageStudio.taForHealthLabelerContainer.relationShipsToggle', defaultMessage: 'Show relationships' }
});

const { styleDiv } = getStylist('TaForHealthLabelerContainer');
const Container = styleDiv('Container', { width: '100%' });

export const TaForHealthLabelerContainer = (props: TaForHealthLabelerContainerProps) => {
    const { document, results, isLabelerRTL, isLabelerWordTokenized } = props;
    const { localize } = useLocalization();
    const [showRelationShips, setShowRelationShips] = React.useState(true);

    const negationSegment = `- ${localize(messages.negative)}`;

    const endIndicesEntityMap = results.entities.reduce((m, e) => m.set(e.offset + e.length - 1, e), new Map<number, HealthcareEntity>());

    const tokenStoreFactory = (token: string, index: number) =>
        new HealthTokenStore(index, token, endIndicesEntityMap.get(index)?.links ?? []);

    const onTokenRender = (tokenProps: ITokenRendererProps<HealthTokenStore>) => (
        <HealthTokenRenderer
            key={tokenProps.tokenStore.index}
            assertions={endIndicesEntityMap.get(tokenProps.tokenStore.index)?.assertion ?? null}
            {...tokenProps}
        />
    );

    const entityAnnotations = results.entities.map<AnnotationData>(e => ({
        id: getId(),
        startToken: e.offset,
        color: getEntityColor(e.category),
        endToken: e.offset + e.length - 1,
        kind: e.isNegated ? 'negativeLabel' : 'label',
        name: `${e.category}${e.isNegated ? negationSegment : ''}`
    }));

    const relationAnnotations = results.relations.map<AnnotationData>(r => {
        const separator = r.relationType.includes('Finds') ? 'Finds' : 'Of';
        let firstEntityName = r.relationType.split(separator)[0];
        firstEntityName = firstEntityName.replace('BodySite', 'BodyStructure');

        const startEntityIndex = firstEntityName === r.entities[0].role ? 0 : 1;
        const secondEntityIndex = 1 - startEntityIndex;

        const startEntity = results.entities[parseInt(r.entities[startEntityIndex].ref.split('/').pop(), 10)];
        const endEntity = results.entities[parseInt(r.entities[secondEntityIndex].ref.split('/').pop(), 10)];

        return {
            id: getId(),
            kind: 'relation',
            name: r.relationType,
            startToken: startEntity.offset,
            color: getEntityColor(endEntity.category),
            endToken: endEntity.offset + endEntity.length - 1
        };
    });

    return (
        <Container data-automation-id="taForHealthLabelerContainer">
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 10 }}>
                <LuisText variant="mediumPlus" fontWeight={500}>
                    {localize(messages.textTitle)}
                </LuisText>

                <Toggle
                    checked={showRelationShips}
                    styles={{ root: { marginBottom: 0 } }}
                    label={localize(messages.relationShipsToggle)}
                    onChange={(_, checked) => setShowRelationShips(checked)}
                />
            </Stack>

            <Labeler
                text={document}
                labelerHeight="unset"
                labelerOverflow="unset"
                maxCharactersPerLine={100}
                onTokenRender={onTokenRender}
                tokenStoreFactory={tokenStoreFactory}
                annotations={[...entityAnnotations, ...(showRelationShips ? relationAnnotations : [])]}
                labelerConfigs={{
                    isRtl: isLabelerRTL,
                    tokenizationType: isLabelerWordTokenized ? 'word' : 'character'
                }}
            />
        </Container>
    );
};
